import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Search, Filter, ChevronLeft, RefreshCw, Clock } from 'lucide-react';

const n   = v => Number(v) || 0;
const fmt = d => { try { return d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'; } catch { return '—'; } };

function waitLabel(hrs) {
  const days = Math.floor(hrs/24), rh = hrs%24;
  return days>0 ? `${days}d ${rh>0?rh+'h':''}`.trim() : `${hrs}h`;
}

export default function DashboardItemsWaitingPage() {
  const navigate = useNavigate();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [dept, setDept]       = useState('all');
  const [depts, setDepts]     = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/dashboard/items-waiting')
      .then(r => {
        const rows = r.data || [];
        setData(rows);
        const unique = [...new Set(rows.map(r=>r.current_stage).filter(Boolean))];
        setDepts(unique);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(item => {
    const q = search.toLowerCase();
    const matchSearch = !q || item.item_name?.toLowerCase().includes(q) || item.item_code?.toLowerCase().includes(q) || item.project_name?.toLowerCase().includes(q) || item.worker_name?.toLowerCase().includes(q);
    const matchDept   = dept==='all' || item.current_stage===dept;
    return matchSearch && matchDept;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={()=>navigate('/admin')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-500"/>
        </button>
        <div>
          <h1 className="text-xl font-black text-gray-900">Items Waiting More Than 24 Hours</h1>
          <p className="text-xs text-gray-400">{filtered.length} items stalled</p>
        </div>
        <button onClick={load} className="ml-auto p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <RefreshCw className={`w-4 h-4 text-gray-400 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-gray-300 flex-shrink-0"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search item, project, worker..."
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-300"/>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-300"/>
          <select value={dept} onChange={e=>setDept(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 outline-none">
            <option value="all">All Stages</option>
            {depts.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} of {data.length} items</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-red-400 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : filtered.length===0 ? (
          <div className="text-center py-16">
            <Clock className="w-12 h-12 text-green-200 mx-auto mb-3"/>
            <p className="text-green-500 font-semibold text-sm">All caught up!</p>
            <p className="text-gray-300 text-xs">No items stalled over 24 hours</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-gray-100 bg-red-50/40">
                  {['#','Item Code','Item Name','Project','Current Stage','Last Work Stage','Worker','Last Work At','Idle'].map(h=>(
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((item,idx)=>{
                  const hrs = n(item.hours_pending);
                  const lbl = waitLabel(hrs);
                  const red = hrs>=48;
                  return (
                    <tr key={item.id} className="hover:bg-red-50/20 cursor-pointer transition-colors"
                      onClick={()=>navigate(`/admin/projects/${item.project_db_id}`)}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-semibold">{idx+1}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.item_code||'—'}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800 text-sm whitespace-nowrap">{item.item_name||item.task_title}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{item.project_name}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{item.current_stage||'—'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{item.last_work_stage||'— (not started)'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{item.worker_name||'—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{item.pending_since ? fmt(item.pending_since) : 'No work yet'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-black ${red?'text-red-500':'text-orange-500'}`}>{lbl}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
